import { useState, useCallback } from 'react';
import { Form, message } from 'antd';

export interface FormModalOptions<T extends Record<string, unknown>> {
  createData?: (data: T) => Promise<void>;
  updateData?: (data: T) => Promise<void>;
  successMessage?: string;
  errorMessage?: string;
}

export function useFormModal<T extends Record<string, unknown>>(
  options: FormModalOptions<T> = {}
) {
  const {
    createData,
    updateData,
    successMessage = '操作成功',
    errorMessage = '操作失败',
  } = options;

  const [visible, setVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);
  const [form] = Form.useForm();

  const handleAdd = useCallback(() => {
    setEditingItem(null);
    form.resetFields();
    setVisible(true);
  }, [form]);

  const handleEdit = useCallback(
    (record: T) => {
      setEditingItem(record);
      form.setFieldsValue(record);
      setVisible(true);
    },
    [form]
  );

  const handleOk = useCallback(async () => {
    try {
      const values = await form.validateFields();

      if (editingItem) {
        // 编辑模式
        if (updateData) {
          const updateDataPayload = {
            ...editingItem,
            ...values,
          };
          await updateData(updateDataPayload);
          message.success('更新成功');
        } else {
          message.error('更新功能未实现');
          return;
        }
      } else {
        // 新增模式
        if (createData) {
          await createData(values);
          console.log('successMessage', successMessage);
          message.success('创建成功');
        } else {
          message.error('创建功能未实现');
          return;
        }
      }

      setVisible(false);
      return true; // 返回成功标识，用于触发数据刷新
    } catch (error) {
      console.error(errorMessage, error);
      const msg = (error as Error)?.message;
      message.error(typeof msg === 'string' && msg ? msg : errorMessage);
      return false;
    }
  }, [editingItem, form, createData, updateData, errorMessage]);

  const handleCancel = useCallback(() => {
    setVisible(false);
  }, []);

  return {
    visible,
    editingItem,
    form,
    handleAdd,
    handleEdit,
    handleOk,
    handleCancel,
  };
}
